import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
//service
import { getDetail, update } from 'services/store.service';
//utils
import { getBase64, mapDataOptions4Select, mapDataOptions, showToast } from 'utils/helpers';
//compnents
import BWAccordion from 'components/shared/BWAccordion/index';
import BWAddress, { DEFAULT_COUNTRY_ID } from 'components/shared/BWAddress';
import BWButton from 'components/shared/BWButton/index';
import FormDebouneSelect from 'components/shared/BWFormControl/FormDebouneSelect';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormItem from 'components/shared/BWFormControl/FormItem';
import FormNumber from 'components/shared/BWFormControl/FormNumber';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import FormTextArea from 'components/shared/BWFormControl/FormTextArea';
import useGetOptions, { optionType } from 'hooks/useGetOptions';
import IpAddress from 'pages/Store/components/IpAddress';
import ModalStock from 'pages/Store/components/ModalStock';
import { sizeTypeOptions, storeStatusOptions } from 'pages/Store/helpers/';
import { getUserOptions } from 'services/user-level-history.service';

const TIME_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
const COORD_REGEX = /^[0-9.,]+$/;

const AGE_STORE_OPTIONS = [
  {
    value: 1,
    label: 'NSO',
  },
  {
    value: 2,
    label: 'LFL',
  },
  {
    value: 3,
    label: 'STU',
  },
];

const ARCHITECTURE_TYPE_OPTIONS = [
  {
    value: 1,
    label: 'Cửa hàng',
  },
  {
    value: 2,
    label: 'Trung tâm/Kho',
  },
];

const DETAIL_VALIDATIONS = {
  open_time: {
    required: 'Giờ mở cửa là bắt buộc.',
    validate: (value) => !value || TIME_REGEX.test(value) || 'Giờ mở cửa không hợp lệ.',
  },
  close_time: {
    required: 'Giờ đóng cửa là bắt buộc.',
    validate: (value) => !value || TIME_REGEX.test(value) || 'Giờ đóng cửa không hợp lệ.',
  },
  province_id: {
    required: 'Tỉnh/Thành phố là bắt buộc.',
  },
  ward_id: {
    required: 'Phường/Xã là bắt buộc.',
  },
  address: {
    required: 'Số nhà, tên đường là bắt buộc.',
  },
  location_x: {
    validate: (value) => !value || COORD_REGEX.test(value) || 'Kinh độ phải là số.',
  },
  location_y: {
    validate: (value) => !value || COORD_REGEX.test(value) || 'Vĩ độ phải là số.',
  },
};

const DETAIL_ERROR_FIELDS = Object.keys(DETAIL_VALIDATIONS);

export default function StoreAdd({ storeId = null, isEdit = true }) {
  const methods = useForm({ shouldUnregister: false });
  const areaData = useGetOptions(optionType.area);
  const companyData = useGetOptions(optionType.company, {
    labelName: 'name',
    valueName: 'id',
  });
  const businessData = useGetOptions(optionType.business, {
    params: { company_id: methods.watch('company_id') },
  });
  const storeTypeData = useGetOptions(optionType.storeType);
  const brandData = useGetOptions(optionType.brand);

  const [modalStock, setModalStock] = useState(undefined);
  const [userData, setUserData] = useState([]);

  const {
    register,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = methods;

  useEffect(() => {
    register('is_active');
    register('banner_url');
    register('store_image_url');
    register('stock_list');
  }, [register]);

  useEffect(() => {
    DETAIL_ERROR_FIELDS.forEach((field) => register(field, DETAIL_VALIDATIONS[field]));
  }, [register]);

  const getData = useCallback(() => {
    if (storeId) {
      getDetail(storeId).then((data) => {
        if (data) {
          reset({
            ...data,
            company_id: data?.company_id ? data.company_id.toString() : data?.company_id,
            business_id: data?.business_id ? Number(data.business_id) : data?.business_id,
            area_id: data?.area_id ? Number(data.area_id) : data?.area_id,
            cluster_id: data?.cluster_id ? Number(data.cluster_id) : data?.cluster_id,
            brand_id: data?.brand_id ? Number(data.brand_id) : data?.brand_id,
            store_type_id: data?.store_type_id ? Number(data.store_type_id) : data?.store_type_id,
            district_id: null,
          });
        }
      });
    } else {
      reset({
        is_active: 1,
        country_id: DEFAULT_COUNTRY_ID,
      });
    }
  }, [reset, storeId]);

  useEffect(getData, [getData]);

  const onSubmit = async (values) => {
    try {
      if (storeId) {
        const storeValues = { ...values };
        await update(storeId, storeValues);
        showToast.success('Cập nhật cửa hàng thành công');
      } else {
        setModalStock(true);
      }
    } catch (error) {
      showToast.error(error.message);
    }
  };

  const onError = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (errors) window.scrollTo(0, 0);
  }, [errors]);

  useEffect(() => {
    getUserOptions('', 1).then((res) => setUserData(res));
  }, []);

  const handleChangeProvince = (value) => {
    methods.clearErrors(['province_id', 'ward_id']);
    methods.setValue('province_id', value);
    methods.setValue('district_id', null);
    methods.setValue('ward_id', null);
  };

  const handleChangeWard = (value) => {
    methods.clearErrors('ward_id');
    methods.setValue('ward_id', value);
  };

  return (
    <React.Fragment>
      <div className='bw_main_wrapp'>
        <div className='bw_row bw_mt_2'>
          <div className='bw_col_12 bw_pb_6'>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit, onError)}>
                <BWAccordion title='Thông tin định danh' id='bw_info' isRequired={true}>
                  <div className='bw_row'>
                    <FormItem className='bw_col_4' label='Mã cửa hàng' isRequired={true}>
                      <FormInput
                        disabled={!isEdit}
                        type='text'
                        field='store_code'
                        placeholder='Mã cửa hàng'
                        validation={{
                          required: 'Mã cửa hàng là bắt buộc',
                          maxLength: {
                            value: 20,
                            message: 'Mã cửa hàng tối đa 20 ký tự.',
                          },
                        }}
                      />
                    </FormItem>
                    <FormItem className='bw_col_8' label='Tên cửa hàng' isRequired={true}>
                      <FormInput
                        type='text'
                        field='store_name'
                        placeholder='Tên cửa hàng'
                        validation={{
                          required: 'Tên cửa hàng là bắt buộc',
                          maxLength: {
                            value: 100,
                            message: 'Tên cửa hàng tối đa 100 ký tự.',
                          },
                        }}
                        disabled={!isEdit}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Công ty' isRequired={true}>
                      <FormSelect
                        disabled={!isEdit}
                        field='company_id'
                        list={mapDataOptions(companyData, { valueAsString: true })}
                        validation={{
                          required: 'Công ty là bắt buộc',
                        }}
                      />
                    </FormItem>
                    <FormItem
                      className='bw_col_4'
                      disabled={!isEdit || !methods.watch('company_id')}
                      label='Chi nhánh'
                      isRequired={true}>
                      <FormSelect
                        disabled={!isEdit || !methods.watch('company_id')}
                        field='business_id'
                        list={mapDataOptions4Select(businessData)}
                        validation={{
                          required: 'Chi nhánh là bắt buộc',
                        }}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Khu vực' isRequired={true}>
                      <FormSelect
                        field='area_id'
                        list={mapDataOptions4Select(areaData)}
                        validation={{
                          required: 'Khu vực là bắt buộc',
                        }}
                        disabled={!isEdit}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Thương hiệu' isRequired={true}>
                      <FormSelect
                        field='brand_id'
                        list={mapDataOptions4Select(brandData)}
                        validation={{
                          required: 'Thương hiệu là bắt buộc',
                        }}
                        disabled={!isEdit}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Loại cửa hàng' isRequired={true}>
                      <FormSelect
                        disabled={!isEdit}
                        type='text'
                        field='store_type_id'
                        placeholder='Loại cửa hàng'
                        list={mapDataOptions4Select(storeTypeData)}
                        validation={{
                          required: 'Loại cửa hàng là bắt buộc',
                        }}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Số điện thoại' isRequired={true}>
                      <FormInput
                        type='text'
                        field='phone_number'
                        placeholder='Số điện thoại'
                        validation={{
                          required: 'Số điện thoại là bắt buộc',
                          pattern: {
                            value: /^[0-9\s-]+$/,
                            message: 'Số điện thoại chỉ gồm số, khoảng trắng hoặc dấu gạch ngang.',
                          },
                          maxLength: {
                            value: 20,
                            message: 'Số điện thoại không hợp lệ.',
                          },
                          minLength: {
                            value: 8,
                            message: 'Số điện thoại không hợp lệ.',
                          },
                        }}
                        disabled={!isEdit}
                      />
                    </FormItem>
                  </div>
                </BWAccordion>

                <BWAccordion title='Thông tin vận hành' id='bw_operation' isRequired={false}>
                  <div className='bw_row'>
                    <FormItem className='bw_col_4' label='Mô hình cửa hàng'>
                      <FormSelect
                        field='architecture_type'
                        list={ARCHITECTURE_TYPE_OPTIONS}
                        disabled={!isEdit}
                        placeholder='Mô hình cửa hàng'
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Tuổi cửa hàng'>
                      <FormSelect
                        field='age_store'
                        list={AGE_STORE_OPTIONS}
                        disabled={!isEdit}
                        placeholder='Tuổi cửa hàng'
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Size'>
                      <FormSelect field='size_type' type='size' list={sizeTypeOptions} placeholder='--Chọn--' />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Diện tích kinh doanh (m²)'>
                      <FormNumber field='acreage' type='number' placeholder='Nhập diện tích' disabled={!isEdit} />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Ngày khai trương'>
                      <FormInput type='date' field='opening_day' disabled={!isEdit} />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Ngày đóng cửa'>
                      <FormInput type='date' field='closing_day' disabled={!isEdit} />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Giờ mở cửa'>
                      <FormInput
                        type='time'
                        field='open_time'
                        placeholder='Giờ mở cửa'
                        validation={DETAIL_VALIDATIONS.open_time}
                        disabled={!isEdit}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Giờ đóng cửa'>
                      <FormInput
                        type='time'
                        field='close_time'
                        placeholder='Giờ đóng cửa'
                        validation={DETAIL_VALIDATIONS.close_time}
                        disabled={!isEdit}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Trạng thái hoạt động' isRequired={true}>
                      <FormSelect
                        field='status_type'
                        list={storeStatusOptions}
                        validation={{
                          required: 'Trạng thái hoạt động là bắt buộc',
                        }}
                        disabled={!isEdit}
                      />
                    </FormItem>
                  </div>
                </BWAccordion>

                <BWAccordion title='Nhân sự phụ trách' id='bw_staff' isRequired={false}>
                  <div className='bw_row'>
                    <FormItem className='bw_col_6' label='Quản lý cửa hàng'>
                      <FormDebouneSelect
                        field='management'
                        fetchOptions={getUserOptions}
                        disabled={!isEdit}
                        allowClear={true}
                        placeholder='Chọn quản lý cửa hàng'
                      />
                    </FormItem>
                    <FormItem className='bw_col_6' label='Phó cửa hàng'>
                      <FormDebouneSelect
                        field='store_assistants'
                        fetchOptions={() => getUserOptions('', 1)}
                        list={mapDataOptions4Select(userData)}
                        mode={'tags'}
                        disabled={!isEdit}
                        allowClear={true}
                        placeholder='Chọn phó cửa hàng'
                      />
                    </FormItem>
                    {!isEdit && (
                      <FormItem className='bw_col_6' label='Số điện thoại quản lý cửa hàng' isRequired={true}>
                        <FormInput disabled type='text' field='phone_number_manager' placeholder='Số điện thoại' />
                      </FormItem>
                    )}
                    <FormItem className='bw_col_12' label='Mô tả'>
                      <FormTextArea field='description' />
                    </FormItem>
                  </div>
                </BWAccordion>

                <BWAccordion title='Địa chỉ cửa hàng' id='bw_address' isRequired={true}>
                  <div className='bw_row'>
                    <FormItem className='bw_col_4' label='Quốc gia' isRequired={true}>
                      <BWAddress
                        field='country_id'
                        type='country'
                        placeholder='Chọn'
                        validation={{
                          required: 'Quốc gia là bắt buộc.',
                        }}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Tỉnh/Thành phố' isRequired={true}>
                      <BWAddress
                        field='province_id'
                        type='province'
                        placeholder='Chọn tỉnh/thành phố'
                        validation={DETAIL_VALIDATIONS.province_id}
                        disabled={!isEdit}
                        onChange={handleChangeProvince}
                      />
                    </FormItem>
                    <FormItem className='bw_col_4' label='Phường/Xã' isRequired={true}>
                      <BWAddress
                        field='ward_id'
                        type='ward'
                        placeholder='Chọn phường/xã'
                        validation={DETAIL_VALIDATIONS.ward_id}
                        disabled={!isEdit}
                        onChange={handleChangeWard}
                      />
                    </FormItem>
                    <FormItem className='bw_col_12' label='Số nhà, tên đường' isRequired={true}>
                      <FormInput
                        type='text'
                        field='address'
                        placeholder='VD: 46 Cửu Long'
                        validation={DETAIL_VALIDATIONS.address}
                        disabled={!isEdit}
                      />
                    </FormItem>
                    <div className='bw_col_12'>
                      <p className='bw_titleS'>
                        Kinh độ - Vĩ độ cửa hàng | <a href='#!'>Tham khảo cách lấy kinh độ, vĩ độ</a>
                      </p>
                      <div className='bw_row'>
                        <FormItem className='bw_col_6' label='Kinh độ'>
                          <FormInput
                            type='text'
                            field='location_x'
                            placeholder='Kinh độ'
                            validation={DETAIL_VALIDATIONS.location_x}
                            disabled={!isEdit}
                          />
                        </FormItem>
                        <FormItem className='bw_col_6' label='Vĩ độ'>
                          <FormInput
                            type='text'
                            field='location_y'
                            placeholder='Vĩ độ'
                            validation={DETAIL_VALIDATIONS.location_y}
                            disabled={!isEdit}
                          />
                        </FormItem>
                      </div>
                    </div>
                    <div className='bw_col_12'>
                      <p className='bw_titleS'>
                        Google Maps | <a href='#!'>Tham khảo cách lấy</a>
                      </p>
                      <div className='bw_frm_box'>
                        <FormTextArea field='map_url' placeholder='Google Maps URL' />
                      </div>
                    </div>
                  </div>
                </BWAccordion>

                <BWAccordion title='Tài khoản ngân hàng' id='bw_bank_com' isRequired={false}>
                  <div className='bw_frm_box'>
                    <p>
                      Tài khoản ngân hàng được quản lý tập trung theo chi nhánh và áp dụng chung cho các cửa hàng
                      thuộc chi nhánh đó.
                    </p>
                    <button
                      type='button'
                      className='bw_btn_outline bw_btn_outline_success bw_mt_1'
                      onClick={() => window._$g.rdr('/bank-user')}>
                      <span className='fi fi-rr-bank' /> Quản lý tài khoản ngân hàng
                    </button>
                  </div>
                </BWAccordion>

                <BWAccordion title='Hình ảnh' id='bw_image' isRequired={false}>
                  <div className='bw_row'>
                    <div className='bw_col_6 bw_text_center'>
                      <b>Banner</b>
                      <div className='bw_load_image bw_mb_2 bw_mt_2 bw_text_center'>
                        <label className='bw_choose_image'>
                          <input
                            type='file'
                            accept='image/*'
                            disabled={!isEdit}
                            onChange={async (_) => {
                              const getFile = await getBase64(_.target.files[0]);
                              methods.setValue('banner_url', getFile);
                            }}
                          />
                          {methods.watch('banner_url') ? (
                            <img alt='banner' style={{ width: '100%' }} src={methods.watch('banner_url') ?? ''}></img>
                          ) : (
                            <span className='fi fi-rr-picture' />
                          )}
                        </label>
                      </div>
                    </div>
                    <div className='bw_col_6 bw_text_center'>
                      <b>Ảnh cửa hàng</b>
                      <div className='bw_load_image bw_mb_2 bw_mt_2 bw_text_center'>
                        <label className='bw_choose_image'>
                          <input
                            type='file'
                            accept='image/*'
                            disabled={!isEdit}
                            onChange={async (_) => {
                              const getFile = await getBase64(_.target.files[0]);
                              methods.setValue('store_image_url', getFile);
                            }}
                          />
                          {methods.watch('store_image_url') ? (
                            <img
                              alt='store'
                              style={{ width: '100%' }}
                              src={methods.watch('store_image_url') ?? ''}></img>
                          ) : (
                            <span className='fi fi-rr-picture' />
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </BWAccordion>

                <BWAccordion title='Địa chỉ IP' id='bw_ip_address' isRequired={false}>
                  <IpAddress isEdit={isEdit} />
                </BWAccordion>

                <BWAccordion title='Trạng thái hệ thống' id='bw_mores' isRequired={false}>
                  <div className='bw_row'>
                    <div className='bw_col_12'>
                      <div className='bw_frm_box'>
                        <div className='bw_flex bw_align_items_center bw_lb_sex'>
                          <label className='bw_checkbox'>
                            <FormInput
                              disabled={!isEdit}
                              type='checkbox'
                              field='is_active'
                              value={watch('is_active')}
                            />
                            <span />
                            Kích hoạt
                          </label>
                          <label className='bw_checkbox'>
                            <FormInput
                              disabled={!isEdit}
                              type='checkbox'
                              field='is_system'
                              value={watch('is_system')}
                            />
                            <span />
                            Hệ thống
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </BWAccordion>

                <div className='bw_btn_save bw_btn_group bw_flex bw_justify_content_right bw_align_items_center'>
                  <BWButton
                    type='success'
                    icon='fi fi-rr-check'
                    content={!isEdit ? 'Chỉnh sửa' : storeId ? 'Hoàn tất chỉnh sửa' : 'Hoàn tất thêm mới'}
                    onClick={() => {
                      if (!isEdit) window._$g.rdr('/store/edit/' + storeId);
                      else handleSubmit(onSubmit, onError)();
                    }}
                    submit={Boolean(isEdit)}></BWButton>
                  <BWButton outline content='Đóng' onClick={() => window._$g.rdr('/store')}></BWButton>
                </div>
                {modalStock && (
                  <ModalStock
                    onClose={() => {
                      setModalStock(false);
                    }}
                  />
                )}
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
